import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Card,
  CardContent,
  Badge,
  Grid,
  Menu,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "../../../../../Config/Hooks";
import { RootState } from "../../../../../Config/Store";
import { fetchFiltersData } from "../sideBarFilterAlarms/SideBarFilterReducer";
import { ADataGridFilter } from "@atiautomacao/ati-ui-library";
import { useDebounce } from "../../../../../Utils/UseDebounce";
import * as MuiIcons from "@mui/icons-material";
import { SITUATION_OPTIONS, FilterState, FilterComponentProps, getInitialFilterState } from "./types";

const iconMap: { [key: string]: React.ComponentType<any> } = {
  'Relé': MuiIcons.ShapeLineOutlined,
  'Inversor': MuiIcons.OfflineBoltOutlined,
  'NCU': MuiIcons.PodcastsOutlined,
  'Tracker': MuiIcons.StackedLineChartOutlined,
  'Sonoro': MuiIcons.VolumeUpOutlined,
  'Ativo': MuiIcons.PriorityHighOutlined,
  'Sem comunicação': MuiIcons.WifiOffOutlined,
  'Não reconhecido': MuiIcons.VisibilityOffOutlined,
  'Crítica': MuiIcons.ErrorOutlined,
  'Urgente': MuiIcons.ErrorOutlined,
};

const getIconComponent = (iconName: string | null | undefined) => {
  if (!iconName) return null;
  
  for (const [key, component] of Object.entries(iconMap)) {
    if (iconName.includes(key)) {
      return component;
    }
  }
  
  return null;
};

export const ChipFilterAlarms: React.FC<FilterComponentProps> = ({
  onApplyFilters,
  hasClearFilters,
}) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
   
  const isFirstRender = useRef(true);
  const [filters, setFilters] = useState<FilterState>(getInitialFilterState);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeMenuCard, setActiveMenuCard] = useState<'equipment' | 'situation' | 'severity' | null>(null);

  const { equipments, severities, loading, errorMessage } = useAppSelector(
    (state: RootState) => state.sideBarFilter
  );

  useEffect(() => {
    dispatch(fetchFiltersData());
  }, [dispatch]);

  useEffect(() => {
    if (errorMessage) {
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  }, [errorMessage, enqueueSnackbar]);

  const handleChipToggle = (
    filterType: keyof FilterState,
    value: string
  ) => {
    setFilters((prev) => {
      const current = prev[filterType];
      return {
        ...prev,
        [filterType]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cardType: 'equipment' | 'situation' | 'severity') => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuCard(cardType);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMenuCard(null);
  };

  const VISIBLE_ITEMS = 5;

  const getFilterValue = (item: any, cardType: 'equipment' | 'situation' | 'severity'): string => {
    if (cardType === 'equipment') {
      return item.id.toString();
    }
    return item.name;
  };


  const renderFilterCard = (
    title: string,
    cardType: "equipment" | "situation" | "severity",
    items: any[],
    filterKey: keyof FilterState,
    getLabel: (item: any) => string,
    visibleItemsCount: number = VISIBLE_ITEMS
  ) => {
    const visibleItems = items.slice(0, visibleItemsCount);
    const hiddenItems = items.slice(visibleItemsCount);
    const hasMoreItems = hiddenItems.length > 0;
    return (
      <Card
        sx={{
          height: "100%",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "0px",
            }}
          >
            <Typography variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                alignItems: 'center',
                display: 'flex',
                gap: '4px'
              }}>
              <MuiIcons.FilterAltOutlined sx={{ color: theme.palette.primary.main }} />
              {title}
            </Typography>
            <Badge
              badgeContent={filters[filterKey].length}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
            <Divider orientation="horizontal" flexItem sx={{ margin: '16px 0' }} />

          {/* Visible Items */}
          <Stack direction="row"  sx={{ flexWrap: "wrap", gap: 1, mb: 0, "& > :not(style) ~ :not(style)": { marginLeft: 0 } }}>
            {visibleItems.map((item) => {
              const itemValue = getFilterValue(item, cardType);
              
              const IconComponent = getIconComponent(item.displayName ?? item.name);
              return (
                <Chip
                  key={item.id || item.name}
                  label={getLabel(item)}
                  onClick={() => handleChipToggle(filterKey, itemValue)}
                  icon={
                    IconComponent ? (
                      <Avatar 
                        alt="Avatar" 
                        variant="circular" 
                        sx={{ 
                          width: 24, 
                          height: 24,
                          backgroundColor: filters[filterKey].includes(itemValue) ? theme.palette.primary.contrastText : theme.palette.grey[300],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <IconComponent 
                          sx={{ 
                            fontSize: 16,
                            color: filters[filterKey].includes(itemValue) ? theme.palette.primary.main : theme.palette.primary.contrastText
                          }} 
                        />
                      </Avatar>
                    ) : undefined
                  }
                  
                  variant={
                    filters[filterKey].includes(itemValue) ? "filled" : "outlined"
                  }
                  color={filters[filterKey].includes(itemValue) ? "primary" : "default"}
                  disabled={loading}
                  size="medium"
                />
              );
            })}

            {hasMoreItems && (
              <Chip
                label={'Outros'}
                onClick={(e) => handleMenuOpen(e, cardType)}
                variant="outlined"
                icon={<Avatar alt="Avatar" variant="circular" sx={{ width: 24, height: 24, backgroundColor: 'transparent' }}>
                      <MuiIcons.ExpandMoreOutlined sx={{color: 'theme.palette.text.primary', fontSize: 20}}/></Avatar>
                }
                size="medium"
                sx={{ cursor: "pointer", marginLeft: 0 }}
              />
            )}
          </Stack>

          {/* Menu para itens ocultos */}
          <Menu
            open={Boolean(menuAnchorEl) && activeMenuCard === cardType}
            anchorEl={menuAnchorEl}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                width: "280px",
                maxHeight: "400px",
              },
            }}
          >
            {hiddenItems.map((item) => {
              const itemValue = getFilterValue(item, cardType);
              return (
                <MenuItem
                  key={item.id || item.name}
                  onClick={() => handleChipToggle(filterKey, itemValue)}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={filters[filterKey].includes(itemValue)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={getLabel(item)} />
                </MenuItem>
              );
            })}
          </Menu>
        </CardContent>
      </Card>
    );
  };

  const { debouncedFn } = useDebounce(3000);

  useEffect(() => {
    if (hasClearFilters) {
      setFilters({
        equipmentTypeIds: [],
        severityName: [],
        situationNames: [],
      });
    }
  }, [hasClearFilters]);

  // Debounce automático de 3 segundos para aplicar filtros
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    debouncedFn(() => {
      
      const appliedFilters: Array<ADataGridFilter> = [];
      let filtersCount = 0;

      if (filters.equipmentTypeIds.length > 0) {
        appliedFilters.push({
          name: "equipmentTypeId",
          condition: "equals",
          value: filters.equipmentTypeIds.join("~"),
          sort: "none",
        });
        filtersCount += filters.equipmentTypeIds.length;
      }

      if (filters.severityName.length > 0) {
        appliedFilters.push({
          name: "severityName",
          condition: "contains",
          value: filters.severityName.join("~"),
          sort: "none",
        });
        filtersCount += filters.severityName.length;
      }

      if (filters.situationNames.length > 0) {
        appliedFilters.push({
          name: "situation",
          condition: "equals",
          value: filters.situationNames.join("~"),
          sort: "none",
        });
        filtersCount += filters.situationNames.length;
      }

      onApplyFilters(appliedFilters, filtersCount);
    });
  }, [filters]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={'12px'}>
        <Grid item xs={12} sm={6} lg={window.innerWidth < 1890 ? 5 : 5.57}>
          {renderFilterCard(
            "Filtro por Equipamento",
            "equipment",
            [...equipments].sort((a, b) => a.sortPriority - b.sortPriority),
            "equipmentTypeIds",
            (item) => item.displayName,
            window.innerWidth < 1890 ? 3 : 5
          )}
        </Grid>

        <Grid item xs={12} sm={6} lg={window.innerWidth < 1890 ? 4 : 4.1}>
          {renderFilterCard(
            "Filtro por Situação",
            "situation",
            SITUATION_OPTIONS,
            "situationNames",
            (item) => item.displayName,
            window.innerWidth < 1890 ? 2 : SITUATION_OPTIONS.length
          )}
        </Grid>

        <Grid item xs={12} sm={6} lg={window.innerWidth < 1890 ? 3 : 2.32}>
          {renderFilterCard(
            "Filtro por Severidade",
            "severity",
            [...severities].sort((a, b) => a.sortPriority - b.sortPriority),
            "severityName",
            (item) => item.displayName || item.name,
            2
          )}
        </Grid>
      </Grid>


    </Box>
  );
};
